import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from '@emotion/styled'
import { CardPropiedad } from '@toctoc/design-system'
import { IPropertyCard } from '@/components/templates/search/interfaces'
import { ePropertyCondition, STATUS } from '@/app/globalContext/globalContext.interfaces'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { fetchRecommendations, selectRecommendedProperties, selectRecommendedPropertiesStatus } from '@/app/reducers/recommendedProperties'
import { addImgSuffix, browserSupportsWebp, preferWebpExtension } from '@/utils/url'
import { getOrderedPrecios, getPreciosForCardPropiedad } from '@/utils/price'

interface Props {
	property: IPropertyCard
	onClose: () => void
	onContact?: () => void
	quoteAndContactEnabled?: boolean
}

const cloudfrontUrl = process.env.NEXT_PUBLIC_CLOUDFRONT_IMG || ''
const FALLBACK_IMG = `${cloudfrontUrl}listaseo/img/imagen-no-viene-cards-vertical.png`

const ArrowLeftIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
		<path d="m12 19-7-7 7-7" /><path d="M19 12H5" />
	</svg>
)

const PhoneIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
		<path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384" />
	</svg>
)

const BedIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ color: '#3200C1' }}>
		<path d="M2 4v16" /><path d="M2 8h18a2 2 0 0 1 2 2v10" /><path d="M2 17h20" /><path d="M6 8v9" />
	</svg>
)

const BathIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ color: '#3200C1' }}>
		<path d="M10 4 8 6" /><path d="M17 19v2" /><path d="M2 12h20" /><path d="M7 19v2" />
		<path d="M9 5 7.621 3.621A2.121 2.121 0 0 0 4 5v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5" />
	</svg>
)

const MaximizeIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ color: '#3200C1' }}>
		<path d="M15 3h6v6" /><path d="m21 3-7 7" /><path d="m3 21 7-7" /><path d="M9 21H3v-6" />
	</svg>
)


const EyeIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
		<path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
		<circle cx="12" cy="12" r="3" />
	</svg>
)

const formatDescription = (html: string): string => {
	return html
		.replace(/<br\s*\/?>/gi, '<br>')
		.replace(/(<br>\s*){2,}/gi, '</p><p>')
		.replace(/(<br>)+$/gi, '')
		.trim()
}

export const MobilePropertyDetailModal = ({ property, onClose, onContact, quoteAndContactEnabled }: Props) => {
	const dispatch = useAppDispatch()
	const recommendedProperties = useAppSelector(selectRecommendedProperties)
	const recommendedStatus = useAppSelector(selectRecommendedPropertiesStatus)

	useEffect(() => {
		const promise = dispatch(fetchRecommendations(property.idProperty))
		return () => promise.abort()
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [property.idProperty])

	const showSimilarSkeleton = recommendedStatus === STATUS.pending || recommendedStatus === STATUS.init
	const showSimilarResults = recommendedStatus === STATUS.idle && recommendedProperties.length > 0

	const [primaryPrice, secondaryPrice] = getOrderedPrecios(property.precios, property.currencyType, property.idEstadoPropiedad)
	const priceUF = primaryPrice?.value ? `${primaryPrice.prefix} ${primaryPrice.value}` : ''
	const priceCLP = secondaryPrice?.value ? `${secondaryPrice.prefix} ${secondaryPrice.value}` : ''
	const priceLabel = property.tipoOperacion === 'arriendo' ? 'Precio de arriendo' : 'Precio de venta'

	const dorm = property.dormitorios?.filter(Boolean)
	const ban = property.bannos?.filter(Boolean)
	const sup = property.superficie?.filter(Boolean)
	const dormText = dorm?.length ? dorm.join('-') : null
	const banText = ban?.length ? ban.join('-') : null
	const supText = sup?.length ? `${sup.join('-')} m²` : null

	const contactLabel = property.idEstadoPropiedad === ePropertyCondition.new ? 'Cotizar' : 'Contactar'

	const supportsWebp = useMemo(() => browserSupportsWebp(), [])
	const galleryImageSrc = useCallback((rawSrc: string) => {
		const base = supportsWebp ? preferWebpExtension(rawSrc) : rawSrc
		return addImgSuffix(base, 'n_wm_')
	}, [supportsWebp])

	const galeria = useMemo(
		() => (property.galeriaImagenes?.filter(Boolean) ?? []).map(galleryImageSrc),
		[property.galeriaImagenes, galleryImageSrc]
	)
	const imgSrc = galeria[0] || (property.imagenPrincipal?.src ? galleryImageSrc(property.imagenPrincipal.src) : FALLBACK_IMG)
	const imgAlt = property.imagenPrincipal?.alt || property.titulo
	const img2Src = galeria[1] || null
	const img3Src = galeria[2] || null

	// Lightbox
	const [lightboxIdx, setLightboxIdx] = useState<number | null>(null)
	const [lightboxImgLoaded, setLightboxImgLoaded] = useState(false)
	const touchStartX = useRef<number | null>(null)

	const openLightbox = (idx: number) => { if (galeria.length > 0) setLightboxIdx(idx) }
	const closeLightbox = () => setLightboxIdx(null)
	const prevImg = useCallback(() => setLightboxIdx(i => i !== null ? (i - 1 + galeria.length) % galeria.length : null), [galeria.length])
	const nextImg = useCallback(() => setLightboxIdx(i => i !== null ? (i + 1) % galeria.length : null), [galeria.length])

	// Al navegar a la siguiente/anterior imagen, vuelve a mostrar el skeleton hasta que cargue.
	useEffect(() => {
		setLightboxImgLoaded(false)
	}, [lightboxIdx])

	useEffect(() => {
		if (lightboxIdx === null) return
		const handler = (e: KeyboardEvent) => {
			if (e.key === 'ArrowLeft') prevImg()
			else if (e.key === 'ArrowRight') nextImg()
			else if (e.key === 'Escape') setLightboxIdx(null)
		}
		window.addEventListener('keydown', handler)
		return () => window.removeEventListener('keydown', handler)
	}, [lightboxIdx, prevImg, nextImg])

	return (
		<Overlay>
			{lightboxIdx !== null && (
				<Lightbox onClick={closeLightbox}>
					<LightboxClose onClick={closeLightbox} aria-label="Cerrar">✕</LightboxClose>
					<LightboxCounter>{lightboxIdx + 1} / {galeria.length}</LightboxCounter>
					<LightboxImageWrapper onClick={e => e.stopPropagation()}>
						{!lightboxImgLoaded && <LightboxSkeleton aria-hidden="true" />}
						<LightboxImg
							$loaded={lightboxImgLoaded}
							src={galeria[lightboxIdx]}
							alt={imgAlt}
							onLoad={() => setLightboxImgLoaded(true)}
							onError={(e) => { (e.currentTarget as HTMLImageElement).src = FALLBACK_IMG; setLightboxImgLoaded(true) }}
							onTouchStart={e => { touchStartX.current = e.touches[0].clientX }}
							onTouchEnd={e => {
								if (touchStartX.current === null) return
								const dx = e.changedTouches[0].clientX - touchStartX.current
								touchStartX.current = null
								if (dx > 50) prevImg()
								else if (dx < -50) nextImg()
							}}
						/>
					</LightboxImageWrapper>
					{galeria.length > 1 && (
						<>
							<LightboxArrow side="left" onClick={e => { e.stopPropagation(); prevImg() }} aria-label="Anterior">‹</LightboxArrow>
							<LightboxArrow side="right" onClick={e => { e.stopPropagation(); nextImg() }} aria-label="Siguiente">›</LightboxArrow>
						</>
					)}
				</Lightbox>
			)}

			<StickyHeader>
				<BackBtn onClick={onClose} aria-label="Volver a resultados">
					<ArrowLeftIcon />
					Volver a resultados
				</BackBtn>
				<div style={{ flex: 1 }} />
				{quoteAndContactEnabled && onContact && (
					<ContactBtn onClick={onContact}>
						<PhoneIcon />
						{contactLabel}
					</ContactBtn>
				)}
			</StickyHeader>

			<ScrollBody>
				<Gallery>
					<MainImg
						src={imgSrc}
						alt={imgAlt}
						style={{ cursor: galeria.length > 0 ? 'zoom-in' : 'default' }}
						onClick={() => openLightbox(0)}
						onError={(e) => { (e.currentTarget as HTMLImageElement).src = FALLBACK_IMG }}
					/>
					<SecondaryStack>
						{img2Src ? (
							<SecondaryImg src={img2Src} alt="" style={{ cursor: 'zoom-in' }} onClick={() => openLightbox(1)} onError={(e) => { (e.currentTarget as HTMLImageElement).src = FALLBACK_IMG }} />
						) : (
							<PlaceholderSlot aria-hidden="true" />
						)}
						{img3Src ? (
							<SecondaryImg src={img3Src} alt="" style={{ cursor: 'zoom-in' }} onClick={() => openLightbox(2)} onError={(e) => { (e.currentTarget as HTMLImageElement).src = FALLBACK_IMG }} />
						) : (
							<PlaceholderSlot aria-hidden="true" />
						)}
					</SecondaryStack>
					{galeria.length > 3 && (
						<GalleryBadge onClick={() => openLightbox(0)}>
							+{galeria.length - 3} fotos
						</GalleryBadge>
					)}
				</Gallery>

				<ContentArea>
					{/* Title + price */}
					<TitleRow>
						<TitleBlock>
							<PropertyTitle>{property.titulo}</PropertyTitle>
							{property.comuna && <PropertyAddress>{property.comuna}</PropertyAddress>}
						</TitleBlock>
						{priceUF && (
							<PriceBlock>
								<PriceUF>{priceUF}</PriceUF>
								{priceCLP && <PriceCLP>{priceCLP}</PriceCLP>}
							</PriceBlock>
						)}
					</TitleRow>

					{/* Stats */}
					{(dormText || banText || supText) && (
						<StatsRow>
							{dormText && (
								<StatChip>
									<BedIcon />
									<StatChipText>
										<StatChipNum>{dormText}</StatChipNum>
										<StatChipLbl>Dormitorios</StatChipLbl>
									</StatChipText>
								</StatChip>
							)}
							{dormText && (banText || supText) && <StatDivider />}
							{banText && (
								<StatChip>
									<BathIcon />
									<StatChipText>
										<StatChipNum>{banText}</StatChipNum>
										<StatChipLbl>Baños</StatChipLbl>
									</StatChipText>
								</StatChip>
							)}
							{banText && supText && <StatDivider />}
							{supText && (
								<StatChip>
									<MaximizeIcon />
									<StatChipText>
										<StatChipNum>{supText}</StatChipNum>
										<StatChipLbl>m² útiles</StatChipLbl>
									</StatChipText>
								</StatChip>
							)}
						</StatsRow>
					)}

					{/* Description */}
					<SectionCard>
						<SectionTitle>Descripción</SectionTitle>
						{property.description ? (
							<DescriptionText dangerouslySetInnerHTML={{ __html: `<p>${formatDescription(property.description)}</p>` }} />
						) : (
							<PlaceholderHint>
								La descripción no está disponible en esta vista.
							</PlaceholderHint>
						)}
					</SectionCard>

					{/* CTAs — misma fila */}
					<CTARow>
						{quoteAndContactEnabled && onContact && (
							<ContactMainBtn onClick={onContact}>
								<PhoneIcon />
								{contactLabel}
							</ContactMainBtn>
						)}
						<VerMasBtn href={`${property.urlFicha}?o=listaseo`} target="_blank" rel="noopener noreferrer">
							<EyeIcon />
							Ver más
						</VerMasBtn>
					</CTARow>

					{/* Propiedades similares (proyectos), vía endpoint centralizado de recomendaciones */}
					{(showSimilarSkeleton || showSimilarResults) && (
						<SimilarSection>
							<SectionTitle>Propiedades similares en {property.comuna || 'la zona'}</SectionTitle>
							<SimilarScroll>
								{showSimilarSkeleton && Array.from({ length: 4 }).map((_, i) => (
									<SimilarSkeletonCard key={i} aria-hidden="true">
										<SimilarSkeletonThumb />
										<SimilarSkeletonLine style={{ width: '60%' }} />
										<SimilarSkeletonLine style={{ width: '85%' }} />
										<SimilarSkeletonLine style={{ width: '50%' }} />
									</SimilarSkeletonCard>
								))}
								{showSimilarResults && recommendedProperties.map(prop => (
									<SimilarCardWrapper key={prop.idProperty}>
										<CardPropiedad
											titulo={prop.titulo}
											comuna={prop.comuna}
											imagenPrincipal={prop.imagenPrincipal}
											imagenInmobiliaria={prop.imagenInmobiliaria}
											urlFicha={`${prop.urlFicha}?o=listaseo`}
											esDestacada={prop.esDestacada}
											esFavorita={prop.esFavorita}
											esOferta={prop.esOferta}
											badge={prop.badge}
											tipoPropiedad={prop.tipoPropiedad}
											tipoOperacion={prop.tipoOperacion}
											precios={getPreciosForCardPropiedad(prop.precios, prop.currencyType, prop.idEstadoPropiedad, prop.tipoOperacion)}
											superficie={prop.superficie}
											dormitorios={prop.dormitorios}
											bannos={prop.bannos}
											oferta={prop.oferta}
											horasPublicado={prop.horasPublicado}
											cardWide={false}
											cloudfrontUrl={cloudfrontUrl}
										/>
									</SimilarCardWrapper>
								))}
							</SimilarScroll>
						</SimilarSection>
					)}

					{/* Coming soon features */}
					{/* <ComingSoonBox>
						<ComingSoonTitle>Próximamente</ComingSoonTitle>
						<TagsRow>
							{['Dibujar zona', 'Buscar cerca de metro', 'Buscar por tiempo de viaje', 'Capas de precio UF/m²', 'Comparar zonas'].map(tag => (
								<Tag key={tag}>{tag}</Tag>
							))}
						</TagsRow>
					</ComingSoonBox> */}
				</ContentArea>
			</ScrollBody>
		</Overlay>
	)
}

/* ─── Styled ───────────────────────────────────────────────────── */

const Overlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 600;
	background: #f9f9f9;
	display: flex;
	flex-direction: column;
	overflow: hidden;
	animation: slideUp 0.3s cubic-bezier(0.32, 0.72, 0, 1);

	@keyframes slideUp {
		from { transform: translateY(100%); }
		to   { transform: translateY(0); }
	}

	@media (min-width: 768px) {
		display: none;
	}
`

const StickyHeader = styled.header`
	position: sticky;
	top: 0;
	z-index: 10;
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 12px 16px;
	background: #ffffff;
	border-bottom: 1px solid #e5e5e5;
	flex-shrink: 0;
`

const BackBtn = styled.button`
	display: flex;
	align-items: center;
	gap: 8px;
	font-family: Nunito, sans-serif;
	font-size: 14px;
	font-weight: 700;
	color: #3200c1;
	background: none;
	border: none;
	cursor: pointer;
	padding: 0;

	&:active { opacity: 0.7; }
`

const ContactBtn = styled.button`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 8px 16px;
	border-radius: 10px;
	background: #3200c1;
	color: #ffffff;
	font-family: Nunito, sans-serif;
	font-size: 14px;
	font-weight: 700;
	border: none;
	cursor: pointer;
	flex-shrink: 0;

	&:active { opacity: 0.85; }
`

const ScrollBody = styled.div`
	flex: 1;
	overflow-y: auto;
	-webkit-overflow-scrolling: touch;
`

/* Gallery */

const Gallery = styled.div`
	position: relative;
	display: grid;
	grid-template-columns: 2fr 1fr;
	gap: 6px;
	height: 240px;
	overflow: hidden;
`

const MainImg = styled.img`
	width: 100%;
	height: 100%;
	object-fit: cover;
	display: block;
`

const SecondaryImg = styled.img`
	flex: 1;
	width: 100%;
	object-fit: cover;
	display: block;
	min-height: 0;
`

const SecondaryStack = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
	height: 100%;
`

const PlaceholderSlot = styled.div`
	flex: 1;
	background: #e5e5e5;
`

/* Content */

const ContentArea = styled.div`
	padding: 16px 16px 40px;
	display: flex;
	flex-direction: column;
	gap: 16px;
	max-width: 640px;
	margin: 0 auto;
	width: 100%;
`

const TitleRow = styled.div`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 16px;
`

const TitleBlock = styled.div`
	flex: 1;
	min-width: 0;
`

const PropertyTitle = styled.h1`
	font-family: Nunito, sans-serif;
	font-size: 20px;
	font-weight: 800;
	color: #343a40;
	margin: 0 0 4px;
	line-height: 1.25;
`

const PropertyAddress = styled.p`
	font-family: Nunito, sans-serif;
	font-size: 14px;
	color: #666;
	margin: 0;
`

const PriceBlock = styled.div`
	flex-shrink: 0;
	text-align: right;
`

const PriceUF = styled.p`
	font-family: Nunito, sans-serif;
	font-size: 22px;
	font-weight: 800;
	color: #3200c1;
	margin: 0;
	line-height: 1.1;
`

const PriceCLP = styled.p`
	font-family: Nunito, sans-serif;
	font-size: 12px;
	color: #666;
	margin: 2px 0 0;
`

/* Stats */

const StatsRow = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 12px 0;
	padding: 14px 16px;
	border-radius: 16px;
	border: 1px solid #b2d0ff;
	background: #eaf2fc;
`

const StatChip = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	flex-shrink: 0;

	svg {
		width: 20px;
		height: 20px;
		flex-shrink: 0;
	}
`

const StatDivider = styled.div`
	width: 1px;
	height: 28px;
	background: #b2d0ff;
	margin: 0 14px;
	flex-shrink: 0;
`

const StatChipText = styled.div``

const StatChipNum = styled.p`
	font-family: Nunito, sans-serif;
	font-size: 15px;
	font-weight: 800;
	color: #343a40;
	margin: 0;
	line-height: 1.2;
	white-space: nowrap;
`

const StatChipLbl = styled.p`
	font-family: Nunito, sans-serif;
	font-size: 10px;
	color: #666;
	margin: 0;
`

/* Section card */

const SectionCard = styled.div`
	background: #ffffff;
	border: 1px solid #e5e5e5;
	border-radius: 16px;
	padding: 18px 20px;
`

const SectionTitle = styled.h2`
	font-family: Nunito, sans-serif;
	font-size: 17px;
	font-weight: 800;
	color: #343a40;
	margin: 0 0 10px;
`

const DescriptionText = styled.div`
	font-family: Nunito, sans-serif;
	font-size: 14px;
	color: #343a40;
	line-height: 1.6;

	p { margin: 0 0 10px; }
	p:last-child { margin-bottom: 0; }
	ul, ol { margin: 0 0 8px; padding-left: 18px; }
	li { margin-bottom: 4px; }
	strong, b { font-weight: 700; }
`

const PlaceholderHint = styled.p`
	font-family: Nunito, sans-serif;
	font-size: 12px;
	color: #999;
	margin: 0;
	line-height: 1.4;
`

/* CTAs */

const CTARow = styled.div`
	display: flex;
	gap: 12px;
	align-items: center;
`

const ContactMainBtn = styled.button`
	flex: 1;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 14px 12px;
	border-radius: 12px;
	background: #3200c1;
	color: #ffffff;
	font-family: Nunito, sans-serif;
	font-size: 14px;
	font-weight: 700;
	border: none;
	cursor: pointer;
	white-space: nowrap;

	&:active { opacity: 0.85; }
`

const VerMasBtn = styled.a`
	flex: 1;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 14px 12px;
	border-radius: 12px;
	background: #ffffff;
	color: #3200c1;
	font-family: Nunito, sans-serif;
	font-size: 14px;
	font-weight: 700;
	text-decoration: none;
	box-shadow: inset 0 0 0 1.5px #3200c1;
	white-space: nowrap;

	&:active { opacity: 0.8; }
`

/* Similar properties */

const SimilarSection = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
`

const SimilarCardWrapper = styled.div`
	flex-shrink: 0;
	width: 190px;
	scroll-snap-align: start;

	&:first-of-type {
		scroll-margin-left: 24px;
	}

	.infoInferior-ds,
	.infoInferior-cardWide-ds {
		display: none !important;
	}
`

const SimilarScroll = styled.div`
	display: flex;
	gap: 12px;
	overflow-x: auto;
	scroll-snap-type: x mandatory;
	scroll-padding-left: 24px;
	-webkit-overflow-scrolling: touch;
	padding-bottom: 2px;
	margin: 0 -16px;
	padding-left: 24px;
	padding-right: 16px;

	&::-webkit-scrollbar { display: none; }
`

const shimmer = `
	@keyframes similarShimmer {
		0% { background-position: -120px 0; }
		100% { background-position: 120px 0; }
	}
`

const SimilarSkeletonCard = styled.div`
	${shimmer}
	display: flex;
	flex-direction: column;
	gap: 6px;
	flex-shrink: 0;
	width: 190px;
	padding: 8px 10px 10px;
	background: #ffffff;
	border: 1px solid #e5e5e5;
	border-radius: 12px;
`

const SimilarSkeletonThumb = styled.div`
	width: 100%;
	height: 100px;
	margin-bottom: 4px;
	border-radius: 6px;
	background: linear-gradient(90deg, #eee 25%, #f5f5f5 37%, #eee 63%);
	background-size: 240px 100%;
	animation: similarShimmer 1.4s ease infinite;
`

const SimilarSkeletonLine = styled.div`
	height: 10px;
	border-radius: 4px;
	background: linear-gradient(90deg, #eee 25%, #f5f5f5 37%, #eee 63%);
	background-size: 240px 100%;
	animation: similarShimmer 1.4s ease infinite;
`

/* Coming soon */

const ComingSoonBox = styled.div`
	background: #f9f9f9;
	border: 1px solid #e5e5e5;
	border-radius: 16px;
	padding: 18px 20px;
`

const ComingSoonTitle = styled.h3`
	font-family: Nunito, sans-serif;
	font-size: 13px;
	font-weight: 700;
	color: #999;
	margin: 0 0 10px;
`

const TagsRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
`

const Tag = styled.span`
	font-family: Nunito, sans-serif;
	font-size: 12px;
	font-weight: 600;
	color: #999;
	background: #ffffff;
	border: 1px solid #e5e5e5;
	border-radius: 999px;
	padding: 5px 12px;
`

/* Gallery badge */

const GalleryBadge = styled.button`
	position: absolute;
	bottom: 10px;
	right: 10px;
	background: rgba(0, 0, 0, 0.55);
	color: #fff;
	font-family: Nunito, sans-serif;
	font-size: 12px;
	font-weight: 700;
	border: none;
	border-radius: 999px;
	padding: 5px 12px;
	cursor: pointer;
	backdrop-filter: blur(4px);
	z-index: 10;

	&:active { opacity: 0.8; }
`

/* Lightbox */

const Lightbox = styled.div`
	position: fixed;
	inset: 0;
	z-index: 700;
	background: rgba(0, 0, 0, 0.95);
	display: flex;
	align-items: center;
	justify-content: center;
`

const LightboxImageWrapper = styled.div`
	position: relative;
	width: min(90vw, 600px);
	height: min(70vh, 450px);
	display: flex;
	align-items: center;
	justify-content: center;
`

const LightboxSkeleton = styled.div`
	position: absolute;
	inset: 0;
	background: #ffffff;
	border-radius: 8px;
	overflow: hidden;

	&::after {
		content: '';
		position: absolute;
		inset: 0;
		background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
		background-size: 200% 100%;
		animation: lightboxSkeletonShimmer 1.5s infinite;
	}

	@keyframes lightboxSkeletonShimmer {
		0% { background-position: 200% 0; }
		100% { background-position: -200% 0; }
	}
`

const LightboxImg = styled.img<{ $loaded: boolean }>`
	max-width: 100%;
	max-height: 100%;
	object-fit: contain;
	display: block;
	user-select: none;
	-webkit-user-drag: none;
	position: relative;
	opacity: ${({ $loaded }) => $loaded ? 1 : 0};
	transition: opacity 0.3s ease-in-out;
`

const LightboxClose = styled.button`
	position: absolute;
	top: 16px;
	right: 16px;
	z-index: 10;
	background: rgba(255, 255, 255, 0.15);
	color: #fff;
	font-size: 20px;
	line-height: 1;
	border: none;
	border-radius: 50%;
	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	backdrop-filter: blur(4px);

	&:active { opacity: 0.7; }
`

const LightboxCounter = styled.div`
	position: absolute;
	top: 20px;
	left: 50%;
	transform: translateX(-50%);
	color: rgba(255, 255, 255, 0.8);
	font-family: Nunito, sans-serif;
	font-size: 13px;
	font-weight: 600;
	pointer-events: none;
`

const LightboxArrow = styled.button<{ side: 'left' | 'right' }>`
	position: absolute;
	top: 50%;
	${({ side }) => side}: 12px;
	transform: translateY(-50%);
	background: rgba(255, 255, 255, 0.15);
	color: #fff;
	font-size: 32px;
	line-height: 1;
	border: none;
	border-radius: 50%;
	width: 48px;
	height: 48px;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	backdrop-filter: blur(4px);
	z-index: 10;

	&:active { opacity: 0.7; }
`
